import { useEffect, useState } from "react";
import { Inbox, Mail, UserPlus, ClipboardCheck, BadgeCheck, XCircle, Loader2, HeartHandshake } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

const QUOTE_FIELDS = [
  ["Company", "company"], ["Role", "role"], ["Team", "teamSize"], ["Project", "projectName"],
  ["Type", "projectType"], ["Engine", "engine"], ["Stage", "stage"], ["Platforms", "platforms"],
  ["Scope", "scope"], ["Budget", "budget"], ["Timeline", "timeline"], ["Start", "startDate"],
  ["NDA", "nda"], ["Support", "ongoingSupport"], ["Refs", "references"],
];

export default function AdminInbox() {
  const [tab, setTab] = useState("quotes");
  const [quotes, setQuotes] = useState([]);
  const [messages, setMessages] = useState([]);
  const [applications, setApplications] = useState([]);
  const [ndis, setNdis] = useState([]);
  const [ndisRegs, setNdisRegs] = useState([]);
  const [busyId, setBusyId] = useState("");
  const [credentials, setCredentials] = useState(null);

  useEffect(() => {
    api.get("/quotes").then((r) => setQuotes(r.data)).catch(() => {});
    api.get("/contact").then((r) => setMessages(r.data)).catch(() => {});
    api.get("/careers/apply").then((r) => setApplications(r.data)).catch(() => {});
    api.get("/admin/ndis/enquiries").then((r) => setNdis(r.data)).catch(() => {});
    api.get("/admin/ndis/participants").then((r) => setNdisRegs(r.data)).catch(() => {});
  }, []);

  const markActioned = async (id) => {
    setBusyId(id);
    try {
      await api.patch(`/admin/ndis/enquiries/${id}`, { status: "actioned", read: true });
      setNdis((list) => list.map((n) => (n.id === id ? { ...n, status: "actioned" } : n)));
      toast.success("Marked as actioned");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusyId("");
    }
  };

  const QUOTE_STATUSES = ["new", "contacted", "quoted", "won", "lost"];
  const setQuoteStatus = async (id, status) => {
    setBusyId(id);
    try {
      await api.patch(`/quotes/${id}`, { status });
      setQuotes((list) => list.map((q) => (q.id === id ? { ...q, status } : q)));
      toast.success(`Quote marked ${status}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusyId("");
    }
  };

  const review = async (appId, action) => {
    setBusyId(appId);
    try {
      const r = await api.post(`/careers/apply/${appId}/${action}`);
      setApplications((list) => list.map((a) => (a.id === appId ? { ...a, reviewed: action === "approve" ? "approved" : "declined" } : a)));
      if (action === "approve") {
        if (r.data.temp_password) setCredentials({ email: applications.find((a) => a.id === appId)?.email, password: r.data.temp_password });
        toast.success("Applicant is now a verified contractor — live in the Directory");
      } else {
        toast.success("Application declined");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusyId("");
    }
  };

  const items = tab === "quotes" ? quotes : tab === "contact" ? messages : tab === "ndis" ? ndis : applications;

  return (
    <section data-testid="admin-inbox-section" className="border border-white/10 bg-black/40">
      <div className="border-b border-white/10 px-6 py-4 flex items-center justify-between gap-3 flex-wrap">
        <h2 className="font-sans font-semibold uppercase tracking-widest text-base text-zinc-100 flex items-center gap-2">
          <Inbox className="w-4 h-4 text-nc-orange" /> Inbox
        </h2>
        <div className="flex gap-2 flex-wrap">
          {[["quotes", `Quotes (${quotes.length})`], ["contact", `Contact (${messages.length})`], ["careers", `Careers (${applications.length})`], ["ndis", `NDIS (${ndis.length})`]].map(([t, label]) => (
            <button
              key={t}
              data-testid={`admin-inbox-tab-${t}`}
              onClick={() => setTab(t)}
              className={`font-mono text-[10px] tracking-[0.2em] uppercase px-3 py-2 border transition-colors duration-300 ${
                tab === t ? "text-nc-orange border-nc-orange/60 bg-nc-orange/10" : "text-zinc-400 border-white/10 hover:border-white/30"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      <div className="p-6 flex flex-col gap-3">
        {credentials && (
          <div data-testid="career-credentials-banner" className="border border-nc-green/40 bg-nc-green/5 p-4 flex items-start justify-between gap-4">
            <div>
              <p className="font-mono text-[10px] tracking-[0.25em] text-nc-green uppercase">New contractor credentials — shown once</p>
              <p className="font-mono text-xs text-zinc-200 mt-2">Login: {credentials.email} · Temp password: <b className="text-nc-green">{credentials.password}</b></p>
              <p className="font-mono text-[9px] text-zinc-500 mt-1">Also emailed to the applicant. They'll be asked to change it.</p>
            </div>
            <button data-testid="career-credentials-dismiss" onClick={() => setCredentials(null)} className="text-zinc-500 hover:text-zinc-100"><XCircle className="w-4 h-4" /></button>
          </div>
        )}
        {items.length === 0 && !(tab === "ndis" && ndisRegs.length > 0) && (
          <p data-testid="admin-inbox-empty" className="font-mono text-[10px] tracking-[0.25em] text-zinc-600 uppercase">Nothing here yet.</p>
        )}
        {tab === "ndis" && ndisRegs.map((r, i) => (
          <div key={r.id} data-testid={`admin-ndis-reg-${i + 1}`} className="border border-nc-orange/30 p-4 bg-[#050505]/50">
            <div className="flex items-baseline justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3 flex-wrap">
                <HeartHandshake className="w-4 h-4 text-nc-orange" />
                <span className="font-sans font-bold uppercase text-sm text-zinc-100">{r.participant_name}</span>
                <a href={`mailto:${r.email}`} className="font-mono text-[11px] text-nc-blue hover:underline">{r.email}</a>
                <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-nc-orange border border-nc-orange/40 px-2 py-0.5">Registered — {r.track === "enterprise" ? "Enterprise" : "Educational"}</span>
                <span className={`font-mono text-[9px] tracking-[0.2em] uppercase border px-2 py-0.5 ${r.status === "agreement_signed" ? "text-nc-green border-nc-green/40" : "text-zinc-400 border-white/15"}`}>
                  {r.status === "agreement_signed" ? "Agreement signed" : "Agreement sent"}
                </span>
              </div>
              <span className="font-mono text-[10px] tracking-[0.2em] text-zinc-500">{String(r.createdAt).slice(0, 10)}</span>
            </div>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-2">
              {[
                ["NDIS Number", r.ndis_number],
                ["Plan", { plan_managed: "Plan-Managed", self_managed: "Self-Managed", agency_managed: "Agency-Managed", not_sure: "Not sure yet" }[r.plan_type] || r.plan_type],
                ["Revenue Cap", r.track === "enterprise" ? `$${r.revenue_cap_aud}/mo` : "—"],
                ["Oversight", r.oversight_email],
                ["Accessibility", (r.accessibility_profiles || []).join(", ")],
                ["Hardware Add-on", r.hardware_addon ? "Yes ($775.96 cap)" : ""],
                ["Invoice", r.invoice ? r.invoice.status : ""],
              ].map(([label, v]) => (v ? (
                <div key={label}>
                  <p className="font-mono text-[8px] tracking-[0.2em] text-zinc-600 uppercase">{label}</p>
                  <p className="font-mono text-[11px] text-zinc-300 mt-0.5 break-words">{String(v)}</p>
                </div>
              ) : null))}
            </div>
            {r.goal && <p className="font-mono text-xs text-zinc-400 leading-relaxed mt-3 whitespace-pre-wrap border-t border-white/10 pt-3">{r.goal}</p>}
          </div>
        ))}
        {items.map((item, i) => (
          <div key={item.id} data-testid={`admin-inbox-${tab}-${i + 1}`} className="border border-white/10 p-4 bg-[#050505]/50">
            <div className="flex items-baseline justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3 flex-wrap">
                {tab === "quotes" ? <ClipboardCheck className="w-4 h-4 text-nc-orange" /> : tab === "contact" ? <Mail className="w-4 h-4 text-nc-blue" /> : tab === "ndis" ? <HeartHandshake className="w-4 h-4 text-nc-orange" /> : <UserPlus className="w-4 h-4 text-nc-green" />}
                <span className="font-sans font-bold uppercase text-sm text-zinc-100">{item.name}</span>
                <a href={`mailto:${item.email}`} className="font-mono text-[11px] text-nc-blue hover:underline">{item.email}</a>
                {item.reference && (
                  <span className="font-mono text-[9px] tracking-[0.2em] text-nc-green border border-nc-green/40 px-2 py-0.5">{item.reference}</span>
                )}
                {(item.disciplines?.length > 0 || item.discipline) && (
                  <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-nc-orange border border-nc-orange/40 px-2 py-0.5">{(item.disciplines || [item.discipline]).filter(Boolean).join(" + ")}</span>
                )}
                {item.portfolio && (
                  <a href={item.portfolio} target="_blank" rel="noopener noreferrer" className="font-mono text-[10px] text-zinc-400 hover:text-nc-blue underline truncate max-w-[240px]">Portfolio</a>
                )}
              </div>
              <span className="font-mono text-[10px] tracking-[0.2em] text-zinc-500">{String(item.createdAt).slice(0, 10)}</span>
            </div>
            {item.message && <p className="font-mono text-xs text-zinc-400 leading-relaxed mt-3 whitespace-pre-wrap">{item.message}</p>}
            {tab === "careers" && (
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                {item.reviewed ? (
                  <span data-testid={`career-reviewed-${i + 1}`} className={`font-mono text-[9px] tracking-[0.2em] uppercase border px-2.5 py-1.5 ${item.reviewed === "approved" ? "text-nc-green border-nc-green/40 bg-nc-green/5" : "text-zinc-500 border-white/15"}`}>
                    {item.reviewed === "approved" ? "Approved — verified contractor" : "Declined"}
                  </span>
                ) : (
                  <>
                    <button data-testid={`career-approve-${i + 1}`} disabled={busyId === item.id} onClick={() => review(item.id, "approve")} className="font-mono text-[9px] tracking-[0.2em] uppercase bg-nc-green/90 hover:bg-nc-green text-black px-3 py-2 transition-colors flex items-center gap-1.5 disabled:opacity-50">
                      {busyId === item.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BadgeCheck className="w-3.5 h-3.5" />} Approve as Verified Contractor
                    </button>
                    <button data-testid={`career-decline-${i + 1}`} disabled={busyId === item.id} onClick={() => review(item.id, "decline")} className="font-mono text-[9px] tracking-[0.2em] uppercase border border-red-500/40 text-red-400 hover:bg-red-500/10 px-3 py-2 transition-colors flex items-center gap-1.5 disabled:opacity-50">
                      <XCircle className="w-3.5 h-3.5" /> Decline
                    </button>
                  </>
                )}
              </div>
            )}
            {tab === "ndis" && (
              <div className="mt-3">
                <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-nc-orange border border-nc-orange/40 bg-nc-orange/5 px-2 py-0.5 inline-block mb-3">NDIS Intake</span>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-2">
                  {[
                    ["Age", item.participant_age],
                    ["Enquiry From", { participant: "The participant", parent_carer: "Parent / Carer", coordinator: "Support Coordinator" }[item.contact_type] || item.contact_type],
                    ["Contact Name", item.contact_name],
                    ["Plan Type", { plan_managed: "Plan-Managed", self_managed: "Self-Managed", agency_managed: "Agency-Managed", not_sure: "Not sure yet" }[item.plan_type] || item.plan_type],
                    ["Phone", item.phone],
                  ].map(([label, v]) => (v ? (
                    <div key={label}>
                      <p className="font-mono text-[8px] tracking-[0.2em] text-zinc-600 uppercase">{label}</p>
                      <p className="font-mono text-[11px] text-zinc-300 mt-0.5 break-words">{String(v)}</p>
                    </div>
                  ) : null))}
                </div>
                <div className="mt-3">
                  {item.status === "actioned" ? (
                    <span data-testid={`ndis-actioned-${i + 1}`} className="font-mono text-[9px] tracking-[0.2em] uppercase text-nc-green border border-nc-green/40 bg-nc-green/5 px-2.5 py-1.5 inline-block">Actioned</span>
                  ) : (
                    <button data-testid={`ndis-action-${i + 1}`} disabled={busyId === item.id} onClick={() => markActioned(item.id)} className="font-mono text-[9px] tracking-[0.2em] uppercase border border-nc-blue/50 text-nc-blue hover:bg-nc-blue/10 px-3 py-2 transition-colors flex items-center gap-1.5 disabled:opacity-50">
                      {busyId === item.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BadgeCheck className="w-3.5 h-3.5" />} Mark as Actioned
                    </button>
                  )}
                </div>
              </div>
            )}
            {tab === "quotes" && (
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-2">
                {QUOTE_FIELDS.map(([label, key]) => {
                  let v = item[key];
                  if (key === "nda") v = item.nda ? "Yes" : "No";
                  if (Array.isArray(v)) v = v.join(", ");
                  if (!v) return null;
                  return (
                    <div key={key}>
                      <p className="font-mono text-[8px] tracking-[0.2em] text-zinc-600 uppercase">{label}</p>
                      <p className="font-mono text-[11px] text-zinc-300 mt-0.5 break-words">{String(v)}</p>
                    </div>
                  );
                })}
                <div className="col-span-full flex items-center gap-2 flex-wrap border-t border-white/5 pt-3 mt-1">
                  <span className="font-mono text-[8px] tracking-[0.2em] text-zinc-600 uppercase">Pipeline</span>
                  <select data-testid={`quote-status-${i + 1}`} value={item.status} disabled={busyId === item.id} onChange={(e) => setQuoteStatus(item.id, e.target.value)} aria-label="Quote pipeline status" className="bg-[#050505] border border-white/15 px-2 py-1 font-mono text-[10px] text-zinc-200 focus:outline-none focus:ring-1 focus:ring-nc-blue">
                    {QUOTE_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <span className="font-mono text-[9px] text-nc-green border border-nc-green/30 px-2 py-0.5">Auto-replied</span>
                  {(item.followups || []).map((f) => (
                    <span key={f.n} data-testid={`quote-followup-${i + 1}-${f.n}`} className="font-mono text-[9px] text-nc-orange border border-nc-orange/30 px-2 py-0.5">Follow-up {f.n} sent {String(f.at).slice(5, 10)}</span>
                  ))}
                  {item.status === "new" && !(item.followups || []).length && <span data-testid={`quote-followup-pending-${i + 1}`} className="font-mono text-[9px] text-zinc-600">auto follow-up at 48h</span>}
                  {item.status === "new" && <span className="font-mono text-[8px] text-zinc-700">follow-ups stop when status moves</span>}
                </div>
              </div>
            )}
            {tab === "quotes" && item.disciplines?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-3">
                {item.disciplines.map((d) => (
                  <span key={d} className="font-mono text-[9px] tracking-[0.15em] uppercase text-nc-blue border border-nc-blue/40 bg-nc-blue/10 px-2 py-0.5">{d}</span>
                ))}
              </div>
            )}
            {tab === "quotes" && item.description && (
              <p className="font-mono text-xs text-zinc-400 leading-relaxed mt-3 whitespace-pre-wrap border-t border-white/10 pt-3">{item.description}</p>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
